import React, { useCallback, useEffect, useRef, useState } from 'react';
import { doc, updateDoc, arrayUnion, getDoc } from 'firebase/firestore';
import { ref as storageRef, uploadString, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../services/firebase';
import { ScreenDrawingOverlay, type DrawingStash } from './ScreenDrawingOverlay';
import type { NodeAttachment } from '../types';

// The recording's draw-on-screen board, usable while just taking notes (no recording). Opened from
// the note's toolbar or Ctrl/⌘+Shift+D; "save to note" attaches the drawing to the note open in
// the expanded view, the same way a drawing made while recording is saved.

export interface QuickDrawTarget { notebookId: string; nodeId: string; title: string }

interface Props {
    open: boolean;
    onClose: () => void;
    target: QuickDrawTarget | null;
    noteDrawings: { id: string; content: string; vectorPath?: string }[];
    onAttachmentsSaved: (attachments: NodeAttachment[], notebookId: string, nodeId: string) => void;
    vn: boolean;
}

export const QuickDrawLayer: React.FC<Props> = ({ open, onClose, target, noteDrawings, onAttachmentsSaved, vn }) => {
    // Stays mounted (hidden) after the first open, so closing and reopening keeps the strokes.
    const [mounted, setMounted] = useState(false);
    useEffect(() => { if (open) setMounted(true); }, [open]);
    const stash = useRef<DrawingStash | null>(null);
    const [toast, setToast] = useState<{ text: string; bad: boolean } | null>(null);
    const show = (text: string, bad = false) => { setToast({ text, bad }); setTimeout(() => setToast(null), bad ? 6000 : 2000); };

    const save = useCallback(async (dataUrl: string, fabricJson?: string): Promise<boolean> => {
        const uid = auth.currentUser?.uid;
        if (!uid || !target) {
            show(vn ? '✗ Hãy mở một note (xem rộng) để lưu hình vẽ vào đó' : '✗ Open a note to save the drawing into it', true);
            return false;
        }
        try {
            const nodeRef = doc(db, `users/${uid}/notebooks/${target.notebookId}/nodes/${target.nodeId}`);
            const attachment: NodeAttachment = { id: Math.random().toString(36).substring(7), type: 'drawing', content: dataUrl, mimeType: 'image/png', createdAt: Date.now() };
            // Editable strokes live in Storage (a note holds at most 1 MB); without them it still saves as a picture.
            if (fabricJson) {
                try {
                    const vectorPath = `users/${uid}/drawings/${attachment.id}.json`;
                    await uploadString(storageRef(storage, vectorPath), fabricJson, 'raw', { contentType: 'application/json' });
                    attachment.vectorPath = vectorPath;
                } catch (e) { console.warn('[quick-draw] strokes not saved; the picture still is', e); }
            }
            await updateDoc(nodeRef, { attachments: arrayUnion(attachment) });
            try {
                const list = (await getDoc(nodeRef)).data()?.attachments as NodeAttachment[] | undefined;
                if (list?.some(a => a.id === attachment.id)) onAttachmentsSaved(list, target.notebookId, target.nodeId);
            } catch { /* saved; shows after a reload */ }
            const t = target.title.length > 40 ? target.title.slice(0, 40) + '…' : target.title;
            show(vn ? `✓ Đã lưu vào note "${t}"` : `✓ Saved to "${t}"`);
            return true;
        } catch (e: any) {
            const tooBig = /exceeds the maximum|maximum allowed size|too large/i.test(String(e?.message || ''));
            show(vn ? `✗ Chưa lưu được: ${tooBig ? 'note đã quá đầy (giới hạn 1 MB), hãy xoá bớt hình vẽ cũ' : (e?.code || e?.message || 'lỗi không rõ')}`
                : `✗ Not saved: ${tooBig ? 'the note is full (1 MB limit)' : (e?.code || e?.message || 'unknown error')}`, true);
            return false;
        }
    }, [target, onAttachmentsSaved, vn]);

    const loadVector = useCallback(async (path: string) => {
        try { const r = await fetch(await getDownloadURL(storageRef(storage, path))); return r.ok ? await r.text() : null; } catch { return null; }
    }, []);

    return (
        <>
            {mounted && <ScreenDrawingOverlay visible={open} onClose={onClose} onSaveToNote={save} noteDrawings={noteDrawings} loadDrawingVector={loadVector} stash={stash} />}
            {toast && (
                <div className={`pointer-events-none fixed top-4 left-1/2 -translate-x-1/2 z-[701] px-4 py-2 ${toast.bad ? 'bg-red-600' : 'bg-green-500'} text-white text-xs font-bold rounded-xl shadow-lg`}>{toast.text}</div>
            )}
        </>
    );
};
